import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBooking } from '../contexts/BookingContext';
import { useBusinessProfile } from '../contexts/BusinessProfileContext';
import { useTheme } from '../contexts/ThemeContext';
import type { Theme } from '../contexts/ThemeContext';
import { ConnectionStatus } from './ConnectionStatus';

type MainLayoutProps = {
  children: ReactNode;
}

type ActiveTab = 'scheduler' | 'operations' | 'configuration';

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const { businessProfile } = useBooking();
  const { businessProfile: profileDetail } = useBusinessProfile();

  // KSeF certificate expiring within 30 days (or expired) — surfaced as a dot
  // on the settings gear, since the KSeF section itself sits in Konfiguracja.
  const certDaysRemaining = profileDetail?.kSeFCertificateDaysRemaining;
  const ksefNeedsAttention =
    profileDetail?.kSeFAuthMode === 'Certificate' &&
    !!profileDetail.kSeFCertificateConfigured &&
    certDaysRemaining !== null &&
    certDaysRemaining !== undefined &&
    certDaysRemaining <= 30;
  const settingsLabel = !ksefNeedsAttention
    ? t('navigation.configuration')
    : certDaysRemaining! <= 0
    ? t('navigation.configurationKsefExpired', 'Konfiguracja: certyfikat KSeF wygasł')
    : t('navigation.configurationKsefExpiring', {
        days: certDaysRemaining,
        defaultValue: 'Konfiguracja: certyfikat KSeF wygasa za {{days}} dni',
      });
  const { theme, setTheme } = useTheme();

  const cycleTheme = () => {
    const next: Theme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
    setTheme(next);
  };

  // Determine active tab based on route and URL params
  const isOperationsPage = location.pathname.startsWith('/operations');
  // The embed widget page (/settings/widget) is opened from Konfiguracja.
  const isConfigPage =
    location.pathname.startsWith('/configuration') || location.pathname.startsWith('/settings');
  const isFullPageRoute = isOperationsPage || isConfigPage;
  const tabFromUrl = searchParams.get('tab');

  const getInitialTab = (): ActiveTab => {
    if (isOperationsPage) return 'operations';
    if (isConfigPage) return 'configuration';
    return 'scheduler';
  };

  const [activeTab, setActiveTab] = useState<ActiveTab>(getInitialTab);

  // Sync activeTab with route/URL changes
  useEffect(() => {
    if (isOperationsPage) {
      setActiveTab('operations');
    } else if (isConfigPage) {
      setActiveTab('configuration');
    }
  }, [isOperationsPage, isConfigPage]);

  // The former Zakupy tab (/?tab=purchases) now lives in Operacje as the
  // "Sprzedaj usługę" and "Przegląd sprzedaży" tiles.
  useEffect(() => {
    if (tabFromUrl === 'purchases') {
      navigate('/operations', { replace: true });
    }
  }, [tabFromUrl, navigate]);

  const handleTabClick = (tab: ActiveTab) => {
    if (tab === 'operations') {
      navigate('/operations');
    } else if (tab === 'configuration') {
      navigate('/configuration');
    } else if (isFullPageRoute) {
      navigate('/');
    } else {
      setActiveTab(tab);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-surface-app">
      {/* Top Header */}
      <header className="bg-surface-dark px-6 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {businessProfile?.avatarUrl ? (
              <img
                src={businessProfile.avatarUrl}
                alt={businessProfile.companyName}
                className="w-10 h-10 rounded-lg object-cover"
              />
            ) : (
              <div className="flex items-center justify-center w-10 h-10 bg-white/15 rounded-lg text-base text-white font-bold">
                {businessProfile?.companyName?.charAt(0).toUpperCase() || 'S'}
              </div>
            )}
            <div>
              <h1 className="font-display text-lg font-semibold tracking-tight text-white">
                {businessProfile?.companyName || 'Spotto'}
              </h1>
              <p className="text-xs text-white/70">
                {businessProfile?.displayName || 'Facility Management'}
              </p>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-end gap-2 self-end -mb-3">
            <button
              onClick={() => handleTabClick('scheduler')}
              className={`px-4 pt-2 pb-3 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'scheduler'
                  ? 'bg-surface-app text-brand-primary dark:text-ink-on-dark-soft'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              {t('navigation.scheduler')}
            </button>
            <button
              onClick={() => handleTabClick('operations')}
              className={`px-4 pt-2 pb-3 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'operations'
                  ? 'bg-surface-app text-brand-primary dark:text-ink-on-dark-soft'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              {t('navigation.operations')}
            </button>
          </div>

          {/* User Info & Actions */}
          <div className="flex items-center gap-4">
            {/* Theme Toggle */}
            <button
              onClick={cycleTheme}
              className="w-10 h-10 flex items-center justify-center bg-white/10 border border-white/30 hover:bg-white/20 text-white rounded-lg transition-colors"
              title={t(`theme.${theme}`)}
              aria-label={t('theme.toggleAriaLabel')}
            >
              {theme === 'light' && (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
              {theme === 'dark' && (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
              {theme === 'system' && (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              )}
            </button>

            {/* Settings (Konfiguracja) */}
            <button
              onClick={() => handleTabClick('configuration')}
              className={`relative w-10 h-10 flex items-center justify-center border text-white rounded-lg transition-colors ${
                activeTab === 'configuration'
                  ? 'bg-white/25 border-white/60'
                  : 'bg-white/10 border-white/30 hover:bg-white/20'
              }`}
              title={settingsLabel}
              aria-label={settingsLabel}
              aria-current={activeTab === 'configuration' ? 'page' : undefined}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {ksefNeedsAttention && (
                <span
                  className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-brand-accent ring-2 ring-surface-dark"
                  aria-hidden="true"
                />
              )}
            </button>

            {/* Real-time Connection Status */}
            <ConnectionStatus />

            {user && (
              <div className="flex items-center gap-3">
                {/* User Avatar */}
                <div className="flex items-center gap-3 px-3 py-2 bg-white/10 border border-white/20 rounded-lg">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-sm">
                      {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                    </div>
                  )}
                  <div className="text-sm">
                    <div className="font-semibold text-white">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-xs text-white/70">{user.email}</div>
                  </div>
                </div>

                {/* Logout Button */}
                <button
                  onClick={signOut}
                  className="px-4 py-2 bg-white/10 border border-white/30 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center gap-2"
                  title="Sign out"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};
